import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import useTranslator from "../../../../hooks/useTranslator";
import { MOBILE_SESSION_STATE } from "../mobileSessionState";
import styles from "../authenticateYourId.module.css";

interface ContinueOnPhoneScreenProps {
  qrUrl: string;
  copyUrl: string;
  sessionState: string;
  onCancel: () => void;
}

const isTerminalState = (state: string): boolean =>
  [
    MOBILE_SESSION_STATE.COMPLETED,
    MOBILE_SESSION_STATE.FAILED,
    MOBILE_SESSION_STATE.EXPIRED,
    MOBILE_SESSION_STATE.CANCELLED,
  ].includes(state as any);

const statusLabels: Record<string, string> = {
  [MOBILE_SESSION_STATE.PENDING]: "connectPhoneStatusPending",
  [MOBILE_SESSION_STATE.PHONE_OPENED]: "connectPhoneStatusOpened",
  [MOBILE_SESSION_STATE.FRONT_CAPTURED]: "connectPhoneStatusFront",
  [MOBILE_SESSION_STATE.BACK_CAPTURED]: "connectPhoneStatusBack",
  [MOBILE_SESSION_STATE.SUBMITTED]: "connectPhoneStatusSubmitted",
  [MOBILE_SESSION_STATE.COMPLETED]: "connectPhoneStatusCompleted",
  [MOBILE_SESSION_STATE.FAILED]: "connectPhoneStatusFailed",
  [MOBILE_SESSION_STATE.EXPIRED]: "connectPhoneStatusExpired",
  [MOBILE_SESSION_STATE.CANCELLED]: "connectPhoneStatusCancelled",
};

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const ContinueOnPhoneScreen = ({
  qrUrl,
  copyUrl,
  sessionState,
  onCancel,
}: ContinueOnPhoneScreenProps) => {
  const { _t } = useTranslator();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [isCopied]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = copyUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setIsCopied(true);
  };

  const isTerminal = isTerminalState(sessionState);
  const isInProgress =
    !isTerminal && sessionState !== MOBILE_SESSION_STATE.PENDING;

  const statusKey = statusLabels[sessionState] || "connectPhoneStatusPending";
  const statusLabel = _t(statusKey);

  const title = isTerminal
    ? getTranslatedText(_t, "connectPhoneTitleDone", "You're all set")
    : getTranslatedText(_t, "continueOnYourPhone", "Continue on your mobile");

  const description = isTerminal
    ? statusLabel
    : getTranslatedText(
        _t,
        "continueOnYourPhoneDescription",
        "Scan the QR code with your phone camera. When you finish the photos, we'll continue here automatically."
      );

  return (
    <div className={`${styles.customKycUploadScreen} ${styles.continueOnPhoneScreen}`}>
      <div className={styles.continueOnPhoneMedia}>
        {isTerminal ? (
          <div
            className={`${styles.continueOnPhoneTerminalIcon} ${
              sessionState === MOBILE_SESSION_STATE.COMPLETED
                ? styles.success
                : styles.error
            }`}
          >
            {sessionState === MOBILE_SESSION_STATE.COMPLETED ? "✓" : "✕"}
          </div>
        ) : (
          <div className={styles.continueOnPhoneQrWrapper}>
            <QRCode
              value={qrUrl}
              size={168}
              level="M"
              bgColor="#ffffff"
              fgColor="#253837"
            />
          </div>
        )}
      </div>

      <div className={styles.continueOnPhoneCopy}>
        <h3>{title}</h3>
        <p>{description}</p>
        {isInProgress && (
          <div
            className={styles.continueOnPhoneStatusBadge}
            role="status"
            aria-live="polite"
          >
            <div className={styles.continueOnPhoneSpinner} aria-hidden="true" />
            <span>{statusLabel}</span>
          </div>
        )}
      </div>

      <div className={styles.customKycUploadActionPanel}>
        {!isTerminal && (
          <>
            <button
              type="button"
              className={styles.customKycUploadPrimaryBtn}
              onClick={handleCopyLink}
            >
              <img src="/images/link-icon.svg" alt="" />
              {isCopied
                ? getTranslatedText(_t, "copied", "Copied")
                : getTranslatedText(_t, "copyLink", "Copy link")}
            </button>
            <button
              type="button"
              className={`${styles.customKycUploadSecondaryBtn} ${styles.continueOnPhoneBtn}`}
              onClick={onCancel}
            >
              {getTranslatedText(
                _t,
                "continueOnComputer",
                "Continue on computer"
              )}
            </button>
          </>
        )}
        {isTerminal && sessionState !== MOBILE_SESSION_STATE.COMPLETED && (
          <button
            type="button"
            className={styles.customKycUploadPrimaryBtn}
            onClick={onCancel}
          >
            {getTranslatedText(_t, "connectPhoneGoBack", "Go back")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContinueOnPhoneScreen;
